import { Component } from '../lib/ComponentBase';
import { simpleComponent } from '../lib/ComponentHelpers';
import type { MoveProvider, DisplayProvider, DisplayInfo, PathfindingNode, WorldState } from '../types/Components';
import type { Entity } from '../types/Entity';
import { HexBearing } from '../grid';
import { ActionType, ActionMode } from '../lib/Action';
import Action from '../lib/Action';
import HexPosition from './HexPosition';

/**
 * An ability that allows an entity to turn.
 * It bids with a low priority to propose a TURN outcome.
 */
class Turn extends Component implements MoveProvider, DisplayProvider {
  static supportedModes = [ActionMode.STANDARD];
  priority: number;

  constructor(data: { priority?: number } = {}) {
    super();
    this.priority = data.priority ?? 0;
  }

  getDisplayInfo(): DisplayInfo {
    return { icon: 'game-icons:clockwise-rotation', label: 'Turn', description: 'Rotates one step clockwise or counter-clockwise per turn.' };
  }

  getPossibleMoves(fromNode: PathfindingNode, _worldState: { walkableCoords: Set<string> }): PathfindingNode[] {
    const { coords, bearing } = fromNode;

    // Clockwise
    const clockwise = HexBearing.next(bearing);

    // Counter-clockwise
    const counterClockwise = HexBearing.previous(bearing);

    return [
      { coords, bearing: clockwise, cost: 1 },
      { coords, bearing: counterClockwise, cost: 1 },
    ];
  }

  getPossiblePlayerActions(entity: Entity, mode: typeof ActionMode[keyof typeof ActionMode], _worldState: WorldState): Map<string, Action> {
    const actions = new Map<string, Action>();
    if (mode !== ActionMode.STANDARD) return actions;

    const selfPosition = entity.getComponent(HexPosition);
    if (!selfPosition) return actions;

    const facingCoords = selfPosition.coords.neighbor(selfPosition.bearing);
    const turnActions: Record<number, Action> = {};

    // TODO consider ditching this feature and only allow tapping the neighbor hexes
    // that you can turn directly to. Will make it more clear when we introduce
    // Swivel that something has changed.
    for (const neighborCoords of selfPosition.coords.allNeighbors()) {
      if (!facingCoords.is(neighborCoords)) {
        const dir = selfPosition.coords.isNeighbor(neighborCoords);
        if (dir !== false) {
          const newBearing = HexBearing.step(selfPosition.bearing, dir);
          if (!turnActions[newBearing]) {
            turnActions[newBearing] = new Action(entity, ActionType.TURN, { bearing: newBearing });
          }
          actions.set(neighborCoords.key, turnActions[newBearing]);
        }
      }
    }
    return actions;
  }
}

export default simpleComponent(Turn);
